import {
  BMComponentsType,
  BMElevationType,
  BMPrimitives,
  BMTabs,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcSystemCrossClose16 from "@traveloka/icon-kit-web/react/IcSystemCrossClose16";
import { useCallback, useMemo, useState } from "react";
import { Platform, Pressable, ScrollView, StyleSheet } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { FullScreenDialog } from "../../../../components";
import BMView from "../../../../components/View/BMView";

export type ContentItem = {
  key: string;
  label: string;
  content: React.ReactNode;
};

type Props = {
  title: string;
  subtitle: string;
  visible: boolean;
  items: ContentItem[];
  FooterComponent?: React.ReactNode;
  onClose: () => void;
};

export default function FlightSummaryDetails({
  title,
  subtitle,
  items,
  visible,
  FooterComponent,
  onClose,
}: Props) {
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const insets = useSafeAreaInsets();
  const { BMComponents, BMElevation } = useBMTheme();
  const styles = useMemo(
    () => createStyles(BMComponents, BMElevation),
    [BMComponents, BMElevation]
  );

  const paddingBottom = useMemo(() => {
    return Platform.select({
      android: BMPrimitives.BMSize.xl3 + insets.bottom,
      default: insets.bottom ? insets.bottom : BMPrimitives.BMSize.md,
    });
  }, [insets.bottom]);

  const handleOnClose = useCallback(() => {
    onClose();
    setActiveIndex(0);
  }, [onClose]);

  return (
    <FullScreenDialog visible={visible}>
      <BMView
        row
        style={[
          styles.header,
          { paddingTop: BMPrimitives.BMSize.sm + insets.top },
        ]}
      >
        <Pressable onPress={handleOnClose}>
          <BMView style={styles.paddingMd}>
            <IcSystemCrossClose16
              width={24}
              height={24}
              color={BMComponents.BMStyleSurfaceBgcolor}
            />
          </BMView>
        </Pressable>
        <BMView flex justify="center" align="center">
          <BMText variant="titleSmall" ink="BMStyleTextInkPrimaryInvert">
            {title}
          </BMText>
          <BMText
            variant="uiNormal"
            ink="BMStyleTextInkSecondaryOncolorbgInvert"
          >
            {subtitle}
          </BMText>
        </BMView>
      </BMView>
      <BMTabs
        items={items}
        onChange={(index) => {
          setActiveIndex(index);
        }}
        activeTabIndex={activeIndex}
      />
      <ScrollView
        bounces={false}
        contentContainerStyle={[
          styles.flex,
          {
            paddingBottom,
          },
        ]}
      >
        {items[activeIndex]?.content}
      </ScrollView>
      {FooterComponent ? (
        <BMView style={styles.footer}>{FooterComponent}</BMView>
      ) : null}
    </FullScreenDialog>
  );
}

function createStyles(
  BMComponents: BMComponentsType,
  BMElevation: BMElevationType
) {
  return StyleSheet.create({
    flex: { flex: 1 },
    header: {
      paddingRight: BMPrimitives.BMSize.xl4,
      backgroundColor: BMComponents.BMStyleSurfaceBgcolorHero,
    },
    paddingMd: {
      padding: BMPrimitives.BMSize.md,
    },
    footer: {
      zIndex: 2,
      backgroundColor: BMComponents.BMStyleSurfaceBgcolor,
      ...BMElevation.elevation2,
    },
  });
}
